import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  ButtonBaseActions,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { SxProps, Theme } from "@mui/material/styles";
import React from "react";
import Draggable from "react-draggable";
import Paper, { PaperProps } from "@mui/material/Paper";
import { COLORS } from "../../assets/theme";
import CustomButton, { ICustomButtonProps } from "../common/CustomButton";
import CustomSelect from "../common/CustomSelect";
import CustomLabel from "./CustomLabel";
import CustomSwitch from "./CustomSwitch";
import CustomTextField from "./CustomTextField";
import AutoComplete, { IAutoCompleteProps } from "./AutoComplete";

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

export type FieldType =
  | {
      type: "text";
      label?: string;
      value: string | number;
      onChange: (value: string | number) => void;
      onBlur?: (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => void;
      endAdornment?: string;
      placeholder?: string;
      error?: boolean;
      helperText?: string;
      bgColor?: string;
      minHeight?: string;
      tooltip?: string;
      fullWidth?: boolean;
      hidden?: boolean;
      size?: "small" | "medium" | "large";
      multiline?: boolean;
      rows?: number;
      disabled?: boolean;
      highlightLabelStar?: boolean;
      validateOnChange?: boolean;
      onValidation?: (isError: boolean) => void;
      typeinput?: string;
      sx?: SxProps<Theme>;
      onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
      min?: number;
      max?: number;
    }
  | {
      type: "select";
      label?: string;
      labelComponent?: React.ReactNode; // <-- Add this line

      value: string | number | (string | number)[];
      onChange: (value: string | number | (string | number)[]) => void;
      onBlur?: (value: string | number | (string | number)[]) => void;
      options: { label: string; value: string | number }[];
      placeholder?: string;
      error?: boolean;
      helperText?: string;
      bgColor?: string;
      fullWidth?: boolean;
      size?: "small" | "medium" | "large";
      multiple?: boolean;
      disabled?: boolean;
      highlightLabelStar?: boolean;
      renderPlaceholder?: boolean;
      selectAll?: boolean;
      sx?: SxProps<Theme>;
      tooltip?: string;
    }
  | {
      type: "autocomplete";
      autocompleteProps: IAutoCompleteProps;
      tooltip?: string;
      highlightLabelStar?: boolean;
    }
  | {
      type: "switch";
      label?: string;
      value: boolean;
      onChange: (value: boolean) => void;
      switchProps?: {
        size?: "small" | "medium";
        color?: "primary" | "secondary" | "default";
      };
      activeText?: string;
      inactiveText?: string;
      disabled?: boolean;
      highlightLabelStar?: boolean;
      tooltip?: string;
    }
  | {
      type: "combined";
      label?: string;
      showLabel?: boolean;
      fields: FieldType[];
      layout?: "row" | "column";
      sx?: SxProps<Theme>;
      highlightLabelStar?: boolean;
    }
  | {
      type: "custom";
      component: React.ReactNode;
      label?: string;
      highlightLabelStar?: boolean;
      fullWidth?: boolean;
    };

interface ICustomDialogProps {
  open: boolean;
  onClose: () => void;
  onNext?: () => void;
  onBack?: () => void;
  title?: string;
  titleAction?: React.ReactNode;
  mode:
    | "add"
    | "edit"
    | "delete"
    | "upload"
    | "send"
    | "Bulk Customer"
    | "Send Rate"
    | "history"
    | "confirm"
    | "confirmPass"
    | "Accounts"
    | "impacted-instructions"
    | "related-instructions"
    | "telnet"
    | "diagnostics"
    | "Bulk Rate / Route"
    | "Bulk MNP / HLR"
    | "updateCurrency"
    | "teams";
  id?: string;
  email?: string;
  tooltip?: string;
  typeinput?: string;
  onSubmit?: (data: { id?: string; email?: string }) => void;
  stepLabels?: string[];
  activeStep?: number;
  fieldsPerRow?: number;
  dialogSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  stepperSx?: SxProps<Theme>;
  type?: string;
  fields?: FieldType[];
  buttons?: ICustomButtonProps[];
  highlightLabelStar?: boolean;
  validateOnChange?: boolean;
  onValidation?: (error: boolean) => void;
  hidden?: boolean;
  selectAll?: boolean;
  dialogData?: { accountType: string };
  renderPlaceholder?: boolean;
  /** Don't move focus back to the opener on close (avoids its focus highlight). */
  disableRestoreFocus?: boolean;
}

const CustomDialog: React.FC<ICustomDialogProps> = ({
  open,
  onClose,
  title,
  titleAction,
  mode,
  selectAll,
  type,
  stepLabels = [],
  activeStep = 0,
  dialogSx,
  titleSx,
  contentSx,
  actionsSx,
  stepperSx,
  fields = [],
  buttons = [],
  highlightLabelStar = false,
  fieldsPerRow = 1,
  disableRestoreFocus,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dialogTitle = stepLabels[activeStep] || title || "";
  const autoFocusActionRef = React.useRef<ButtonBaseActions>(null);

  const groupedFields = React.useMemo(() => {
    const fieldsPerRowValue = Math.max(fieldsPerRow || 1, 1);

    return fields.reduce<FieldType[][]>((acc, field) => {
      const isFullWidthCustomField = field.type === "custom" && field.fullWidth;

      if (isFullWidthCustomField) {
        acc.push([field]);
        return acc;
      }

      const currentRow = acc[acc.length - 1];
      const isCurrentRowFullWidthCustom =
        currentRow?.length === 1 &&
        currentRow[0].type === "custom" &&
        currentRow[0].fullWidth;

      if (
        !currentRow ||
        currentRow.length >= fieldsPerRowValue ||
        isCurrentRowFullWidthCustom
      ) {
        acc.push([field]);
      } else {
        currentRow.push(field);
      }

      return acc;
    }, []);
  }, [fields, fieldsPerRow]);

  return (
    <Dialog
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: isSmallScreen ? 2 : 4,
          minWidth: isSmallScreen ? "100vw" : "650px",
          maxWidth: isSmallScreen
            ? "100vw"
            : fieldsPerRow > 1 || stepLabels?.length > 0
              ? "900px"
              : "600px",
          width: isSmallScreen ? "100vw" : undefined,
          minHeight: isSmallScreen ? "auto" : undefined,
          maxHeight: isSmallScreen ? "90vh" : undefined,
          height: isSmallScreen ? "auto" : undefined,
          padding: isSmallScreen ? 2 : 4,
          paddingTop: isSmallScreen ? 2 : 2,
          borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          boxSizing: "border-box",
          overflow: isSmallScreen ? "hidden" : undefined,
          position: isSmallScreen ? "fixed" : "relative",
          bottom: isSmallScreen ? 0 : undefined,
          boxShadow: isSmallScreen
            ? "0 -4px 20px rgba(0, 0, 0, 0.15)"
            : "0 8px 32px rgba(0, 0, 0, 0.12)",
          ...(isSmallScreen && {
            margin: 0,
            top: "auto",
          }),
          ...(type === "confirmPass" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "300px",
            minHeight: isSmallScreen ? "auto" : "200px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(type === "import" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "890px",
            minHeight: isSmallScreen ? "auto" : "200px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(type === "confirm" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "1000px",
            minHeight: isSmallScreen ? "auto" : "300px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "delete" && {
            padding: isSmallScreen ? 3 : 1,
            gap: 2,
            minWidth: isSmallScreen ? "100vw" : "250px",
            minHeight: isSmallScreen ? "auto" : "250px",
            maxWidth: isSmallScreen ? "100vw" : "400px",
            textAlign: "center",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "Bulk Customer" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "550px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "500px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "Accounts" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "500px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "500px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "history" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "1050px",
            minHeight: isSmallScreen ? "auto" : "300px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "Bulk Rate / Route" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "900px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "500px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "Bulk MNP / HLR" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 3,
            minWidth: isSmallScreen ? "100vw" : "600px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "700px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "impacted-instructions" && {
            padding: 0,
            gap: 0,
            minHeight: "0",
            maxWidth: "none",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "related-instructions" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 2,
            minWidth: isSmallScreen ? "100vw" : "1050px",
            minHeight: isSmallScreen ? "auto" : "300px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "telnet" && {
            padding: 0,
            gap: 0,
            minHeight: "0",
            width: isSmallScreen ? "100vw" : "800px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "teams" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 2,
            minWidth: isSmallScreen ? "100vw" : "900px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "1150px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...(mode === "diagnostics" && {
            padding: isSmallScreen ? 3 : 2,
            gap: 2,
            minWidth: isSmallScreen ? "100vw" : "750px",
            minHeight: isSmallScreen ? "auto" : "300px",
            maxWidth: isSmallScreen ? "100vw" : "750px",
            borderRadius: isSmallScreen ? "24px 24px 0 0" : "16px",
          }),
          ...dialogSx,
        },
      }}
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullScreen={isSmallScreen}
      fullWidth={true}
      maxWidth={false}
      disableRestoreFocus={disableRestoreFocus}
      // autoFocus alone loses the race with MUI's focus trap under StrictMode.
      TransitionProps={{
        onEntered: () => autoFocusActionRef.current?.focusVisible(),
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          position: "relative",
          textAlign: "left",
          borderBottom: "2px solid " + COLORS.typographyColor + "48",
          cursor: isSmallScreen ? "default" : "move",
          paddingLeft: isSmallScreen ? "16px" : "5px",
          paddingRight: isSmallScreen ? "56px" : "5px",
          paddingTop: isSmallScreen ? "20px" : "16px",
          paddingBottom: isSmallScreen ? "16px" : "16px",
          touchAction: isSmallScreen ? "none" : "auto",
          ...titleSx,
        }}
      >
        {stepLabels?.length > 0 ? (
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isSmallScreen}
            orientation={isSmallScreen ? "horizontal" : "horizontal"}
            sx={{
              width: "100%",
              flexGrow: 1,
              overflowX: isSmallScreen ? "auto" : "visible",
              "& .MuiStepConnector-root": { display: "none" },
              "& .MuiStep-root": {
                padding: isSmallScreen ? "0 4px" : "0 8px",
                minWidth: isSmallScreen ? "auto" : "120px",
                minHeight: isSmallScreen ? "40px" : "64px",
                flex: isSmallScreen ? "0 0 auto" : "1 1 0px",
              },
              "& .MuiStepLabel-root": {
                flexDirection: isSmallScreen ? "column" : "column",
                alignItems: "center",
                textAlign: "center",
              },
              ...stepperSx,
            }}
          >
            {stepLabels.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={() => null}
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: index === activeStep ? "600" : "normal",
                      color:
                        index === activeStep
                          ? COLORS.BlackBackground
                          : COLORS.dividerColor,
                      cursor: "pointer",
                      fontSize: isSmallScreen ? "0.75rem" : "1rem",
                      lineHeight: isSmallScreen ? "1.2" : "1.5",
                      textAlign: "center",
                      whiteSpace: isSmallScreen ? "nowrap" : "normal",
                      overflow: isSmallScreen ? "hidden" : "visible",
                      textOverflow: isSmallScreen ? "ellipsis" : "unset",
                      maxWidth: isSmallScreen ? "80px" : "none",
                      display: "block",
                    },
                  }}
                >
                  {isSmallScreen && label.length > 10 ? (
                    <Tooltip title={label} arrow placement="top">
                      <span>{label}</span>
                    </Tooltip>
                  ) : (
                    label
                  )}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CustomLabel
              text={dialogTitle}
              fontWeight={600}
              fontSize="1.5rem"
              sx={{ margin: 0 }}
            />
            {titleAction}
          </Box>
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            minWidth: isSmallScreen ? 48 : 40,
            minHeight: isSmallScreen ? 48 : 40,
            padding: isSmallScreen ? 2 : 1,
            zIndex: 1,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:active": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: isSmallScreen ? 28 : 24 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          paddingTop: isSmallScreen ? "16px" : "5px",
          paddingBottom: isSmallScreen ? "24px" : "5px",
          paddingLeft: isSmallScreen ? "16px" : "5px",
          paddingRight: isSmallScreen ? "16px" : "5px",
          overflowY: isSmallScreen ? "auto" : "visible",
          maxHeight: isSmallScreen ? "calc(90vh - 200px)" : undefined,
          ...(contentSx as any),
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {mode === "delete" ? (
            <Box>
              <p>Are you sure you want to delete this</p>
            </Box>
          ) : (
            groupedFields.map((row, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection:
                    isSmallScreen || fieldsPerRow === 1 ? "column" : "row",
                  alignItems: "center",
                }}
              >
                {row.map((field, fieldIndex) => {
                  const fieldHighlight =
                    field.highlightLabelStar ?? highlightLabelStar;

                  switch (field.type) {
                    case "text": {
                      const textField = (
                        <CustomTextField
                          key={fieldIndex}
                          label={field.label}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          error={field.error}
                          helperText={field.helperText}
                          endAdornment={field.endAdornment}
                          placeholder={field.placeholder}
                          bgColor={field.bgColor}
                          hidden={field.hidden}
                          fullWidth
                          minHeight={field.minHeight}
                          multiline={field.multiline}
                          validateOnChange={field.validateOnChange}
                          rows={field.rows}
                          type={field.typeinput}
                          onValidation={field.onValidation}
                          size={field.size === "large" ? "medium" : field.size}
                          disabled={field.disabled}
                          highlightLabelStar={fieldHighlight}
                          onKeyDown={field.onKeyDown}
                          inputProps={{
                            min: field.min,
                            max: field.max,
                          }}
                        />
                      );
                      return field.tooltip ? (
                        <Tooltip
                          title={field.tooltip}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -8], // Adjust the second value to change the space between the tooltip and the text field
                                },
                              },
                            ],
                          }}
                          key={fieldIndex}
                        >
                          {textField}
                        </Tooltip>
                      ) : (
                        textField
                      );
                    }
                    case "select": {
                      const selectField = (
                        <CustomSelect
                          label={field.label}
                          labelComponent={field.labelComponent}
                          value={field.value}
                          multiSelect={field.multiple}
                          placeholder={field.placeholder}
                          renderPlaceholder={field.renderPlaceholder}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          options={field.options || []}
                          bgColor={field.bgColor}
                          selectAll={field.selectAll}
                          error={field.error}
                          helperText={field.helperText}
                          fullWidth
                          size={field.size}
                          disabled={field.disabled}
                          highlightLabelStar={fieldHighlight}
                        />
                      );
                      return field.tooltip ? (
                        <Tooltip
                          key={fieldIndex}
                          title={field.tooltip}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [0, -8] },
                              },
                            ],
                          }}
                        >
                          <Box sx={{ width: "100%", display: "block" }}>
                            {selectField}
                          </Box>
                        </Tooltip>
                      ) : (
                        <React.Fragment key={fieldIndex}>
                          {selectField}
                        </React.Fragment>
                      );
                    }
                    case "autocomplete": {
                      const autocompleteField = (
                        <AutoComplete
                          {...field.autocompleteProps}
                          highlightLabelStar={
                            field.autocompleteProps.highlightLabelStar ??
                            field.highlightLabelStar ??
                            fieldHighlight
                          }
                          sx={{
                            width: "100%",
                            ...field.autocompleteProps.sx,
                          }}
                        />
                      );
                      return field.tooltip ? (
                        <Tooltip
                          key={fieldIndex}
                          title={field.tooltip}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [0, -8] },
                              },
                            ],
                          }}
                        >
                          <Box sx={{ width: "100%", display: "block" }}>
                            {autocompleteField}
                          </Box>
                        </Tooltip>
                      ) : (
                        <React.Fragment key={fieldIndex}>
                          {autocompleteField}
                        </React.Fragment>
                      );
                    }
                    case "switch": {
                      const switchField = (
                        <CustomSwitch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          label={field.label || ""}
                          size={field.switchProps?.size || "medium"}
                          activeText={field.activeText}
                          inactiveText={field.inactiveText}
                          disabled={field.disabled}
                          highlightLabelStar={fieldHighlight}
                        />
                      );
                      return field.tooltip ? (
                        <Tooltip
                          key={fieldIndex}
                          title={field.tooltip}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [0, -8] },
                              },
                            ],
                          }}
                        >
                          <Box sx={{ width: "100%", display: "block" }}>
                            {switchField}
                          </Box>
                        </Tooltip>
                      ) : (
                        <React.Fragment key={fieldIndex}>
                          {switchField}
                        </React.Fragment>
                      );
                    }
                    case "combined":
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection:
                              field.layout === "column" ? "column" : "row",
                            gap: 4,
                            width: "100%",
                          }}
                        >
                          <Box
                            key={fieldIndex}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              width: "100%",
                            }}
                          >
                            {field.showLabel && field.label && (
                              <CustomLabel
                                text={field.label}
                                sx={{ margin: 0 }}
                                highlightLabelStar={fieldHighlight}
                              />
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: isSmallScreen
                                  ? "column"
                                  : field.layout === "column"
                                    ? "column"
                                    : "row",
                                gap: 2,
                                ...field.sx,
                              }}
                            >
                              {field.fields.map((subField, subFieldIndex) => {
                                switch (subField.type) {
                                  case "text":
                                    return (
                                      <CustomTextField
                                        key={subFieldIndex}
                                        label={subField.label}
                                        value={subField.value}
                                        onChange={(e) =>
                                          subField.onChange(e.target.value)
                                        }
                                        endAdornment={subField.endAdornment}
                                        error={subField.error}
                                        helperText={subField.helperText}
                                        placeholder={subField.placeholder}
                                        bgColor={subField.bgColor}
                                        minWidth="100px"
                                        multiline={subField.multiline}
                                        rows={subField.rows}
                                        disabled={subField.disabled}
                                        highlightLabelStar={
                                          subField.highlightLabelStar || false
                                        }
                                        sx={subField.sx}
                                      />
                                    );
                                  case "select":
                                    return (
                                      <CustomSelect
                                        key={subFieldIndex}
                                        label={subField.label}
                                        value={subField.value}
                                        onChange={subField.onChange}
                                        options={subField.options || []}
                                        placeholder={subField.placeholder}
                                        bgColor={subField.bgColor}
                                        minWidth={300}
                                        error={subField.error}
                                        helperText={subField.helperText}
                                        size={subField.size}
                                        disabled={subField.disabled}
                                        renderPlaceholder={true}
                                        highlightLabelStar={
                                          subField.highlightLabelStar || false
                                        }
                                      />
                                    );
                                  case "custom":
                                    return (
                                      <Box key={subFieldIndex} sx={{ flex: 1 }}>
                                        {subField.component}
                                      </Box>
                                    );
                                  default:
                                    return null;
                                }
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );

                    case "custom":
                      return (
                        <React.Fragment key={fieldIndex}>
                          {field.component}
                        </React.Fragment>
                      );
                    default:
                      return null;
                  }
                })}
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          padding: isSmallScreen ? "16px 16px 24px 16px" : undefined,
          gap: isSmallScreen ? 2 : 1,
          ...actionsSx,
        }}
      >
        {buttons.map((button, index) => (
          <CustomButton
            key={index}
            action={button.autoFocus ? autoFocusActionRef : undefined}
            label={button.label}
            onClick={button.onClick}
            bgColor={button.bgColor || COLORS.GOLD}
            textColor={button.textColor || COLORS.WhiteBackground}
            borderColor={button.borderColor}
            size={button.size || "medium"}
            variant={button.variant || "contained"}
            borderRadius={button.borderRadius || "12px"}
            disabled={button.disabled || false}
          />
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default CustomDialog;
